import { useState } from "react";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import type { Job } from "@/data/types";

interface ContractorRatingPanelProps {
  job: Job;
  onRatingSubmitted?: () => void;
}

export function ContractorRatingPanel({ job, onRatingSubmitted }: ContractorRatingPanelProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const contractorName = job.assignedCrew || job.assignedSubcontractor || "Your Contractor";

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error("Please select a rating before submitting");
      return;
    }

    setSubmitted(true);
    toast.success(`Thank you! Your ${rating}-star rating has been submitted.`);
    onRatingSubmitted?.();
  };

  if (submitted) {
    return (
      <div className="text-center py-8">
        <Star className="h-12 w-12 text-warning mx-auto mb-4 fill-current" />
        <h3 className="text-lg font-semibold mb-2">Rating Submitted</h3>
        <p className="text-sm text-muted-foreground">
          Thank you for rating your experience with {contractorName}.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-bold mb-3">Rate Your Contractor</h3>
        <p className="text-xs text-muted-foreground mb-2">
          How would you rate your experience with {contractorName}?
        </p>
        <div className="flex items-center gap-1 mb-4">
          {[1, 2, 3, 4, 5].map((star) => {
            const filled = star <= (hoverRating || rating);
            return (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="transition-transform hover:scale-110"
              >
                <Star
                  className={`h-8 w-8 ${
                    filled
                      ? "text-warning fill-warning"
                      : "text-muted-foreground/30"
                  }`}
                />
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-xs font-medium mb-1.5">Additional Feedback (Optional)</p>
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What did you like about working with your contractor?"
          className="min-h-[80px] text-xs"
        />
      </div>

      <Button onClick={handleSubmit} className="w-full">
        Submit Rating
      </Button>
    </div>
  );
}